import { useNavigate } from "@tanstack/react-router"
import { ButtonLink } from "design-system/atoms/Button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Plus, SearchIcon } from "lucide-react"
import CampaignsFormSelector from "modules/campaigns/components/Form/CampaignsFormSelector"
import { useWhatsappOpeningMessagesQuery } from "modules/campaigns/hooks/sections/openingMessages/useWhatsappOpeningMessagesQuery"
import { WhatsappTabs } from "modules/settings/components/integrations/whatsapp/WhatsappTabs"
import { Pagination } from "modules/shared/components/Pagination"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSearchQuery } from "modules/shared/hooks/useSearchQuery"
import { useSortBy } from "modules/shared/hooks/useSortBy"
import { useTranslation } from "react-i18next"
import { Route as WhatsappOpeningMessagesRoute } from "routes/_base/_auth/settings/channels/whatsapp/openings/index"
import { whatsappOpeningMessagesSortFields, WhatsappOpeningMessagesSortSchema } from "schema"
import z from "zod"
import { WhatsappOpeningMessagesTable } from "./WhatsappOpeningMessagesTable"

const ITEMS_PER_PAGE = 30
type WhatsappOpeningMessagesSortFields = z.infer<
  typeof WhatsappOpeningMessagesSortSchema
>["sortBy"]["field"]

const OPENING_MESSAGE_ARCHIVED = ["active", "archived"] as const

export const WhatsappOpeningMessages = () => {
  const { t } = useTranslation()
  const search = WhatsappOpeningMessagesRoute.useSearch()
  const navigate = useNavigate()
  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: WhatsappOpeningMessagesRoute.fullPath,
    total: ITEMS_PER_PAGE,
  })

  const filters = {
    archived: search.filterBy?.archived ? "archived" : "active",
    campaignId: search.filterBy?.campaignId,
  }

  const { query, setQuery } = useSearchQuery({
    search,
    fullPath: WhatsappOpeningMessagesRoute.fullPath,
    onSearchQueryChange: () => setPage(1),
  })

  const { onSort, sortBy, fields } = useSortBy<WhatsappOpeningMessagesSortFields[]>({
    search,
    fullPath: WhatsappOpeningMessagesRoute.fullPath,
    fields: [...whatsappOpeningMessagesSortFields],
  })

  const { data, isPending } = useWhatsappOpeningMessagesQuery({
    query: {
      query,
      page,
      total: ITEMS_PER_PAGE,
      sortBy,
      filterBy: {
        campaignId: search.filterBy?.campaignId?.toString(),
        archived: search.filterBy?.archived?.toString(),
      },
    },
    onSuccess: (data) => setCount(data.pageInfo.count),
  })

  const onArchiveFilterChange = (option: string) => {
    const newFilterBy = { ...search.filterBy }

    if (option === "active") {
      delete newFilterBy.archived
    } else {
      newFilterBy.archived = "true"
    }

    navigate({
      to: "/settings/channels/whatsapp/openings",
      search: {
        ...search,
        filterBy: newFilterBy,
      },
    })
  }

  const onFilterByCampaignId = (campaignId?: string) => {
    const newFilterBy = { ...search.filterBy }

    if (campaignId) {
      newFilterBy.campaignId = campaignId
    } else {
      delete newFilterBy.campaignId
    }

    navigate({
      to: "/settings/channels/whatsapp/openings",
      search: {
        ...search,
        filterBy: newFilterBy,
      },
    })
  }

  return (
    <>
      <WhatsappTabs />
      <div className="flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row gap-2 justify-end">
          <div className="transition-all duration-200 w-full opacity-100 pointer-events-auto">
            <div className="flex flex-col sm:flex-row gap-2">
              <SearchQuery
                placeholder={t("campaigns.templates.search")}
                setQuery={setQuery}
                defaultValue={query}
                icon={SearchIcon}
              />
              <CampaignsFormSelector
                value={filters.campaignId}
                emptyPlaceholder={t(
                  "campaigns.templates.openingMessages.filters.campaigns.emptyPlaceholder",
                )}
                onChange={(campaign) => onFilterByCampaignId(campaign?.value)}
                placeholder={t("campaigns.templates.openingMessages.filters.campaigns.placeholder")}
                searchPlaceholder={t(
                  "campaigns.templates.openingMessages.filters.campaigns.searchPlaceholder",
                )}
              />
              <Select
                defaultValue={filters.archived}
                onValueChange={(value) => onArchiveFilterChange(value)}
              >
                <SelectTrigger className="w-fit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...OPENING_MESSAGE_ARCHIVED].map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`campaigns.templates.openingMessages.filters.archived.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <ButtonLink
              variant="default"
              to="/settings/channels/whatsapp/openings/create"
              className="h-full"
            >
              <span>
                <Plus className="size-4" strokeWidth={2} />
              </span>
              <span>{t("campaigns.templates.openingMessages.actions.create")}</span>
            </ButtonLink>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <WhatsappOpeningMessagesTable
            data={data}
            fields={fields}
            isPending={isPending}
            onSort={onSort}
            search={search}
          />
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            count={count}
          />
        </div>
      </div>
    </>
  )
}
